import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { supabase, getStoredUser } from "../supabase";
import PostTime, { postNameColumn } from "../components/PostTime";
import DownloadButton from "../components/DownloadButton";
import LikeButton from "../components/LikeButton";
import SaveButton from "../components/SaveButton";
import Comments from "../components/Comments";
import ImageLightbox from "../components/ImageLightbox";
import TruncatedText from "../components/TruncatedText";
import ImageCarousel from "../components/ImageCarousel";
import ShareButton from "../components/ShareButton";
import LazyImage from "../components/LazyImage";
import { IoFlame, IoPricetag, IoFlag, IoEllipsisVertical } from "react-icons/io5";
import { PiChatCircleBold } from "react-icons/pi";
import { getBlockedUserIds } from "../utils/blocks";
import { getMutedUserIds } from "../utils/mutes";
import PostSkeleton from "../components/PostSkeleton";
import { likeDesign } from "../utils/likes";
import { fetchMyEngagement, subscribeToMyEngagement } from "../utils/myEngagement";
import { subscribeToStatsPolling } from "../utils/statsPolling";
import ReportModal from "../components/ReportModal";
import PostMenu from "../components/PostMenu";
import VerifiedBadge from "../components/VerifiedBadge";
import TagInfoModal from "../components/TagInfoModal";
import { useLanguage } from "../context/LanguageContext";
import { confirmLogin } from "../utils/dialog";

const PAGE_SIZE = 10;
// Ranking needs designs sorted by score, but nothing needs the FULL set of
// every post made this week/month — capping how many the ranked query can
// return keeps this page's cost flat as the total post count grows.
// "This Week" only ever shows a top 20; "This Month" shows a top 30.
const TRENDING_CAP = { week: 20, month: 30 };

export default function Trending() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [designs, setDesigns] = useState([]);
  const [range, setRange] = useState("week");
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(getStoredUser);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [tagModalId, setTagModalId] = useState(null);
  const [reportModalId, setReportModalId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openComments, setOpenComments] = useState({});

  // The database already returns the top TRENDING_CAP designs pre-ranked
  // (see fetchTrending), so pagination here just reveals more of that
  // already-fetched, already-sorted list — no extra reads per page.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);
  const menuRef = useRef(null);

  // Close ⋮ menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    async function getUser() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    }

    getUser();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchTrending() {
      setLoading(true);

      const daysBack = range === "week" ? 7 : 30;
      const fromDate = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000);

      const {
        data: { session }
      } = await supabase.auth.getSession();
      const currentUser = session?.user ?? null;
      const [blockedRes, mutedRes] = currentUser
        ? await Promise.all([getBlockedUserIds(currentUser.id), getMutedUserIds(currentUser.id)])
        : [[], []];
      const blockedIds = [...new Set([...blockedRes, ...mutedRes])];

      // Switching "This Week"/"This Month" quickly can let an older,
      // slower-to-resolve request for the previous range land after the
      // newer one and overwrite it with the wrong range's results.
      if (cancelled) return;

      // Score (likes + downloads) is a generated column in Postgres, so the
      // database does the ranking and only the top TRENDING_CAP rows ever
      // come back — this used to fetch every post in the window and sort
      // it in the browser, which got slower and more expensive as posts
      // piled up.
      // Many designs can tie on score (e.g. 0 likes + 0 downloads), and
      // Postgres doesn't guarantee a stable order among ties on its own —
      // without a tiebreaker, which designs land inside TRENDING_CAP (and
      // in what order) could shuffle between identical fetches even though
      // nothing about them changed.
      let query = supabase
        .from("designs")
        .select("*, users(username, avatar_url, verified)")
        .gte("created_at", fromDate.toISOString())
        .order("score", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(TRENDING_CAP[range]);

      if (blockedIds.length > 0) {
        query = query.not("user_id", "in", `(${blockedIds.join(",")})`);
      }

      const { data, error } = await query;

      if (cancelled) return;

      if (error) {
        console.log("TRENDING ERROR:", error);
        setLoading(false);
        return;
      }

      let list = data || [];
      if (currentUser) {
        const { likedIds, savedIds } = await fetchMyEngagement(list.map(d => d.id), currentUser.id);
        if (cancelled) return;
        list = list.map(d => ({ ...d, _likedByMe: likedIds.has(d.id), _savedByMe: savedIds.has(d.id) }));
      }

      // comment_count lives directly on designs (a trigger-maintained
      // column, same idea as likes/downloads) — no separate query needed.
      setDesigns(list);
      setVisibleCount(PAGE_SIZE);
      setLoading(false);
    }

    fetchTrending();

    return () => {
      cancelled = true;
    };
  }, [range]);

  // Infinite scroll: reveal PAGE_SIZE more of the already-fetched, already-
  // ranked list once the sentinel at the bottom comes into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || loading || visibleCount >= designs.length) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          setVisibleCount(prev => Math.min(prev + PAGE_SIZE, designs.length));
        }
      },
      { rootMargin: "400px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, visibleCount, designs.length]);

  // Keeps the latest `designs` for the poll below to read without a stale
  // closure — it runs on its own timer, not in response to designs changing.
  const designsRef = useRef(designs);
  useEffect(() => {
    designsRef.current = designs;
  }, [designs]);

  // Refreshes likes/downloads/comment_count for whatever's currently loaded
  // on this page, every 30s — not a realtime listener, since a plain
  // "designs" UPDATE subscription has no way to scope itself to just the
  // posts on screen and would broadcast every design's change to every
  // connected client regardless (see utils/statsPolling.js). Whether *this
  // viewer* liked/saved each one is a separate, per-user concern — see the
  // myEngagement effect below.
  useEffect(() => {
    return subscribeToStatsPolling(
      () => designsRef.current.map(d => d.id),
      rows => {
        const byId = new Map(rows.map(r => [r.id, r]));
        setDesigns(prev =>
          prev.map(d => {
            const fresh = byId.get(d.id);
            return fresh
              ? { ...d, likes: fresh.likes, downloads: fresh.downloads, comment_count: fresh.comment_count }
              : d;
          })
        );
      }
    );
  }, []);

  // Live updates for this viewer's OWN likes/saves — e.g. liking something
  // from another tab or device signed into the same account. Filtered to
  // just this user's rows (see utils/myEngagement.js).
  useEffect(() => {
    if (!user) return undefined;

    const setEngagement = (designId, fields) => {
      setDesigns(prev => prev.map(d => (d.id === designId ? { ...d, ...fields } : d)));
    };

    return subscribeToMyEngagement(user.id, {
      onLike: id => setEngagement(id, { _likedByMe: true }),
      onUnlike: id => setEngagement(id, { _likedByMe: false }),
      onSave: id => setEngagement(id, { _savedByMe: true }),
      onUnsave: id => setEngagement(id, { _savedByMe: false })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // 🔄 Re-fetch the live comment count for one design (used when closing the panel)
  async function refreshCommentCount(designId) {
    const { count, error } = await supabase
      .from("comments")
      .select("id", { count: "exact", head: true })
      .eq("design_id", designId);

    if (error) {
      console.log("REFRESH COUNT ERROR:", error);
      return;
    }

    setDesigns(prev =>
      prev.map(d => (d.id === designId ? { ...d, comment_count: count ?? d.comment_count } : d))
    );
  }

  function toggleComments(id) {
    const isCurrentlyOpen = !!openComments[id];

    setOpenComments(prev => ({ ...prev, [id]: !isCurrentlyOpen }));

    if (isCurrentlyOpen) {
      refreshCommentCount(id);
    }
  }

  function requireLogin(action) {
    if (user) {
      action();
      return;
    }
    confirmLogin(t("common.logInToContinue"), t("nav.login")).then(goLogin => {
      if (goLogin) navigate("/login");
    });
  }

  return (
    <div style={page}>
      {/* One row instead of a tall centred heading with the buttons under it:
          the title on the left, the week/month switch on the right. */}
      <div style={headerRow}>
        <h1 style={title}>
          <IoFlame size={20} style={{ color: "var(--accent-gold)", flexShrink: 0 }} />
          <span style={titleText}>{t("trending.title")}</span>
        </h1>

        <div style={filters} role="group" aria-label="Time range">
          <button style={range === "week" ? active : btn} onClick={() => setRange("week")} aria-pressed={range === "week"}>
            {t("trending.thisWeek")}
          </button>
          <button style={range === "month" ? active : btn} onClick={() => setRange("month")} aria-pressed={range === "month"}>
            {t("trending.thisMonth")}
          </button>
        </div>
      </div>

      {loading ? (
        <PostSkeleton />
      ) : designs.length === 0 ? (
        <p style={mutedText}>
          No trending designs {range === "week" ? "this week" : "this month"} yet.
        </p>
      ) : (
        <div style={grid}>
          {designs.slice(0, visibleCount).map((design, index) => {
            const commentsOpen = !!openComments[design.id];
            const commentCount = design.comment_count || 0;

            function updateDesign(fields) {
              setDesigns(prev =>
                prev.map(d => (d.id === design.id ? { ...d, ...fields } : d))
              );
            }

            return (
              <div key={design.id} style={card}>
                <div style={cardHeader}>
                  <Link
                    to={design.user_id === user?.id ? "/profile" : `/u/${design.user_id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flex: 1,
                      minWidth: 0,
                      textDecoration: "none",
                      color: "var(--text-primary)"
                    }}
                  >
                    <LazyImage
                      src={design.users?.avatar_url || "/default-avatar.svg"}
                      alt={`${design.users?.username || "User"}'s avatar`}
                      style={{ ...cardAvatar, flexShrink: 0 }}
                    />
                    <span style={postNameColumn}>
                      <span style={usernameLink}>
                        @{design.users?.username || "unknown"}
                        {design.users?.verified && <VerifiedBadge />}
                      </span>
                      <PostTime date={design.created_at} />
                    </span>
                  </Link>
                  <div
                    style={{ position: "relative", marginLeft: "auto", flexShrink: 0 }}
                    ref={openMenuId === design.id ? menuRef : null}
                  >
                    <button
                      type="button"
                      style={reportBtn}
                      onClick={() => setOpenMenuId(openMenuId === design.id ? null : design.id)}
                      aria-label="More options"
                    >
                      <IoEllipsisVertical />
                    </button>
                    {openMenuId === design.id && (
                      <PostMenu
                        items={[
                          { icon: <IoPricetag />, label: "Tag info", onClick: () => { setOpenMenuId(null); setTagModalId(design.id); } },
                          ...(user?.id && design.user_id !== user.id
                            ? [{ icon: <IoFlag />, label: "Report", onClick: () => { setOpenMenuId(null); setReportModalId(design.id); } }]
                            : [])
                        ]}
                      />
                    )}
                  </div>
                </div>

                <div style={imageWrap}>
                  <ImageCarousel
                    images={design.images?.length > 0 ? design.images : [design.image_url]}
                    alt={design.title}
                    imgStyle={{ ...image, cursor: "zoom-in" }}
                    onImageClick={setLightboxImage}
                    onDoubleTap={() =>
                      likeDesign({ designId: design.id, user, designOwnerId: design.user_id, onUpdate: updateDesign, likeOnly: true })
                    }
                  />
                  {/* Every design is numbered by its place in the ranking; the top
                      three also get the flame. */}
                  <span style={rankBadge}>
                    #{index + 1}
                    {index < 3 && <> <IoFlame style={{ verticalAlign: -2 }} /></>}
                  </span>
                </div>

                <div style={content}>
                  <div style={actions}>
                    <LikeButton
                      designId={design.id}
                      likes={design.likes}
                      liked={design._likedByMe}
                      user={user}
                      designOwnerId={design.user_id}
                      onUpdate={updateDesign}
                    />

                    <button
                      onClick={() => requireLogin(() => toggleComments(design.id))}
                      style={commentBadgeBtn}
                      aria-label="Toggle comments"
                    >
                      <PiChatCircleBold size={27} /> <span style={commentCountText}>{commentCount}</span>
                    </button>

                    <DownloadButton
                      fileURL={design.design_url}
                      files={design.design_files}
                      designId={design.id}
                      downloads={design.downloads}
                      designOwnerId={design.user_id}
                      user={user}
                      onUpdate={updateDesign}
                    />
                    <ShareButton designId={design.id} />
                    <span style={{ display: "inline-flex", marginLeft: "auto" }}>
                      <SaveButton
                        designId={design.id}
                        saved={design._savedByMe}
                        user={user}
                        onUpdate={updateDesign}
                      />
                    </span>
                  </div>

                  <p style={caption}><TruncatedText text={design.title} /></p>
                  {design.description && (
                    <p style={{ margin: "2px 0", color: "var(--text-body)", fontSize: "14px" }}>
                      {design.description}
                    </p>
                  )}

                  {commentsOpen && (
                    <Comments designId={design.id} onClose={() => toggleComments(design.id)} designOwnerId={design.user_id} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && visibleCount < designs.length && <div ref={sentinelRef} style={{ height: 1 }} />}

      {lightboxImage && (
        <ImageLightbox
          images={lightboxImage.images}
          initialIndex={lightboxImage.index}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {tagModalId && (
        <TagInfoModal designId={tagModalId} onClose={() => setTagModalId(null)} />
      )}

      {reportModalId && (
        <ReportModal
          reporterId={user?.id}
          targetType="design"
          targetId={reportModalId}
          onClose={() => setReportModalId(null)}
        />
      )}
    </div>
  );
}

/* ===== STYLES (Virsa palette, matches Home/Saved) ===== */

const page = {
  padding: "8px 4px calc(90px + env(safe-area-inset-bottom))",
  background: "var(--bg-page)",
  minHeight: "100vh"
};

const headerRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "0 10px 8px"
};

// Always one line: on a narrow phone the title gives way (…) before the
// switch does, instead of the switch dropping to a second row.
const title = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
  margin: 0,
  fontSize: 20,
  fontWeight: 800,
  color: "var(--text-heading)"
};

const titleText = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const mutedText = { fontSize: 14, color: "var(--text-muted)", textAlign: "center" };

// A single pill holding both choices, the selected one filled in.
const filters = {
  display: "inline-flex",
  flexShrink: 0,
  marginLeft: "auto",
  gap: 2,
  padding: 3,
  borderRadius: 999,
  background: "var(--bg-soft)",
  border: "1px solid var(--border-soft)"
};

const btn = {
  padding: "5px 12px",
  borderRadius: 999,
  border: "none",
  cursor: "pointer",
  background: "transparent",
  color: "var(--text-secondary)",
  fontWeight: 600,
  fontSize: 12.5,
  whiteSpace: "nowrap"
};

const active = {
  ...btn,
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)"
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
  gap: "10px 18px"
};

const card = {
  background: "var(--bg-surface)",
  borderRadius: 14,
  overflow: "hidden",
  border: "1px solid var(--border-soft)",
  boxShadow: "0 4px 14px rgba(59,10,20,0.08)"
};

const reportBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  border: "none",
  background: "none",
  color: "var(--text-primary)",
  fontSize: 22,
  cursor: "pointer"
};

const cardHeader = {
  display: "flex",
  alignItems: "center",
  gap: 24,
  padding: "4px 14px"
};

const cardAvatar = {
  width: 46,
  height: 46,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-medium)"
};

const imageWrap = { position: "relative" };

const image = {
  width: "100%",
  height: "auto",
  display: "block"
};

const rankBadge = {
  position: "absolute",
  top: 10,
  left: 10,
  background: "rgba(20, 14, 8, 0.75)",
  color: "var(--accent-contrast)",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 700
};

const content = { padding: "12px 12px 4px" };

const usernameLink = {
  fontWeight: 700,
  color: "var(--text-primary)",
  textDecoration: "none",
  fontSize: 18
};

const caption = {
  margin: "4px 0",
  fontSize: 13.5,
  color: "var(--text-secondary)",
  whiteSpace: "pre-wrap"
};


const actions = {
  display: "flex",
  alignItems: "center",
  gap: 2
};

const commentBadgeBtn = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "none",
  border: "none",
  borderRadius: 20,
  height: 40,
  padding: "0 6px",
  fontSize: 14,
  fontWeight: 700,
  color: "var(--text-primary)",
  cursor: "pointer"
};

const commentCountText = {
  fontWeight: "bold",
  fontSize: 13,
  color: "var(--text-primary)"
};
